import { TeamUserRolesEnum } from "@/enums/team-user-role";
import type { PromptOwnerType, TeamUserRole } from "@shaarp/common";

type AutoUseDecider = {
  readonly ownerType: PromptOwnerType;
  /** Role on the team the request will carry, i.e. the selected one. */
  readonly selectedTeamRole: TeamUserRole | undefined;
  readonly isPlatformGod: boolean;
};

/**
 * Whether this user may decide the automatic use of a tactic of this owner type.
 *
 * Mirrors `assertCanSetAutoUse` on the API for the one case the browser can
 * answer: a team tactic, where the server reads the role of the team named by
 * the `team-id` header — the selected one, and no other. Holding a manager or
 * admin role on a different team says nothing here, and offering the switch on
 * that basis produces a refused save with nothing on screen to explain it.
 *
 * The public case is left to whatever gate opens the dialog: the API checks a
 * platform admin level the browser has no view of, so answering it here would
 * take the switch away from admins who are entitled to it.
 */
export const canDecideAutoUse = ({
  ownerType,
  selectedTeamRole,
  isPlatformGod,
}: AutoUseDecider): boolean => {
  if (ownerType !== "team") {
    return true;
  }

  return (
    isPlatformGod ||
    selectedTeamRole === TeamUserRolesEnum.MANAGER ||
    selectedTeamRole === TeamUserRolesEnum.ADMIN
  );
};

type AutoUseMover = Omit<AutoUseDecider, "ownerType"> & {
  /** Who the tactic belongs to today, before the form is saved. */
  readonly currentOwnerType: PromptOwnerType;
  /** Whether the assistant applies it today. */
  readonly isAutoEnabled: boolean;
};

/**
 * Whether this user may hand a tactic to a different owner.
 *
 * Leaving is a decision as much as arriving: a switched-on team tactic taken
 * back into someone's own space stops applying to every colleague it applied to
 * a moment ago. The API guards both ends (`assertCanSetAutoUse`, called on the
 * origin and on the destination), so the picker has to ask the same question —
 * a refused save is one request, and it loses the title and content typed
 * alongside.
 *
 * A tactic the assistant never applies moves freely: there is no decision to
 * take away from anyone.
 */
export const canTakeAutoUsedTacticOut = ({
  currentOwnerType,
  isAutoEnabled,
  selectedTeamRole,
  isPlatformGod,
}: AutoUseMover): boolean =>
  !isAutoEnabled ||
  canDecideAutoUse({
    ownerType: currentOwnerType,
    selectedTeamRole,
    isPlatformGod,
  });

type OwnerChange = {
  readonly savedOwnerType: PromptOwnerType;
  readonly savedIsAutoEnabled: boolean;
  readonly nextOwnerType: PromptOwnerType;
};

/**
 * The automatic-use switch's value in the edit form once the owner type has
 * been changed, while the author has not touched the switch themselves (#740).
 *
 * Making an existing tactic public is how a platform tactic gets published,
 * so it gets the same default a brand new public one does: off, since the
 * shared catalogue reaches every customer the moment it is switched on. Any
 * other move keeps the saved value — going back to the saved type included,
 * so trying a type and changing one's mind restores the switch.
 */
export const autoEnabledAfterOwnerChange = ({
  savedOwnerType,
  savedIsAutoEnabled,
  nextOwnerType,
}: OwnerChange): boolean =>
  nextOwnerType === "public" && savedOwnerType !== "public"
    ? false
    : savedIsAutoEnabled;
